/*
 * @Author: dgq 370541946@qq.com
 * @Date: 2025-05-15 16:22:34
 * @FilePath: /fgk-wxapp/src/common/areaUtils.js
 * @Description: 地区工具类 查找等
 */

import {AreaParser} from './areaParser'
import {getStore, setStore} from './store'
import {getAreaList} from '@/api/insurance/policy'

/**
 * 设置省市区数据
 */
export async function setAreaList() {
	try {
		// 省市区
		let area_list = getStore({name: 'area_list'}) || []
		//获取缓存
		if (area_list.length > 0) {
			return
		}
		// 省市区
		//调用接口获取数据，采用 async await
		const resAreaData = await getAreaList(3)
		let {data = []} = resAreaData.data || {}
		if (data.length <= 0) {
			throw new Error('获取省市区数据失败，数据为空！')
		}
		//存储
		setStore({name: 'area_list', content: data, type: 'session'})
	} catch (error) {
		console.log('areaUtils------->', '获取省市区数据失败', error)
	}
}

/**
 * 获取省市区 3级数据 并且存储
 * @param {number} level - 地区层级(1:省, 2:市, 3:区)，默认3
 * @returns {Array} 省市区数据
 */
export function getAreaLevelList(level = 3) {
	try {
		// 省市区
		let area_list = getStore({name: 'area_list'}) || []
		//获取缓存
		if (area_list.length <= 0) {
			throw new Error('省市区数据为空！')
		}
		//根据level 获取数据
		if (level === 1) {
			// 只返回省级数据，清空children
			return area_list.map((province) => ({
				...province,
				children: [],
			}))
		} else if (level === 2) {
			// 返回省市数据，保留省和市，清空区县children
			return area_list.map((province) => ({
				...province,
				children: province.children
					? province.children.map((city) => ({
							...city,
							children: [],
					  }))
					: [],
			}))
		} else {
			// level === 3，返回完整的省市区数据
			return area_list
		}
	} catch (error) {
		console.log('areaUtils------->', '获取省市区数据失败', error)
	}
}

/**
 * 通过地区code值获取省市区在各自层级数组中的索引位置
 * @param {string} code - 地区编码
 * @param {number} level - 地区层级(1:省, 2:市, 3:区)，默认3
 * @returns {Array} 索引位置数组 [省索引, 市索引, 区索引]
 */
export function getSelectDefaultIndexArea(code, config = {}) {
	try {
		// 获取完整的地区数据
		let area_list = getStore({name: 'area_list'}) || []
		if (area_list.length <= 0) {
			throw new Error('省市区数据为空！')
		}
		const result = [] // 默认值，表示未找到
		if (!code || code.length < 6) {
			return result
		}
		//code 转换为集合
		let areaSet = getAreaSetByCode(code)
		//配置项
		let {level = 3, valueName = 'areaCode'} = config || {}
		// 查找省级索引
		const provinceIndex = area_list.findIndex((province) => province[valueName] === areaSet[0])

		if (provinceIndex === -1) {
			return result // 未找到省份
		}
		result.push(provinceIndex)
		// 只需要省级索引
		if (level === 1) {
			return result // 只需要省级索引
		}
		// 查找市级索引
		const province = area_list[provinceIndex]
		if (!province.children) {
			return result
		}
		const cityIndex = province.children.findIndex((city) => city[valueName] === areaSet[1])
		if (cityIndex === -1) {
			return result // 未找到城市
		}
		result.push(cityIndex)
		if (level === 2) {
			return result // 只需要省市级索引
		}
		// 查找区级索引
		const city = province.children[cityIndex]
		if (!city.children) {
			return result
		}
		const districtIndex = city.children.findIndex((district) => district[valueName] === areaSet[2])
		if (districtIndex !== -1) {
			result.push(districtIndex)
		}
		return result
	} catch (error) {
		console.error('获取地区索引失败:', error)
		return []
	}
}

/**
 * 获取自定义选择项的默认索引
 * @param {string} value - 选择项的值
 * @param {Array} options - 选择项
 * @param {Object} config - 配置项
 * @returns {Array} 默认索引
 */
export function getSelectDefaultIndexCustom(value, options, config = {}) {
	let {valueName = 'value'} = config || {}
	let index = options.findIndex((item) => item[valueName] === value)
	return index === -1 ? [] : [index]
}

/**
 * 获取通过code省市区内容 从缓存里拿的地区数据
 * @param {string} code - 地区编码
 * @param {number} level - 地区层级(1:省, 2:市, 3:区)，默认3
 * @param {Object} options - 配置项
 * @param {string} options.separator - 分隔符，默认'-'
 * @returns {string} 地区名称
 */
export function getAreaNameByCode(code, level = 3, options = {}) {
	try {
		let area_list = getStore({name: 'area_list'}) || []
		if (area_list.length <= 0) {
			throw new Error('省市区数据为空！')
		}
		//获取
		return new AreaParser(area_list, options).getAreaName(code, level)
	} catch (error) {
		throw error
	}
}

/**
 * 判断一个地区是否存在
 * @param {string} code - 地区编码
 * @param {number} level - 地区层级(1:省, 2:市, 3:区)，默认3
 * @param {Object} options - 配置项
 * @param {string} options.separator - 分隔符，默认'-'
 * @param {boolean} options.useCache - 是否使用缓存，默认true
 * @returns {boolean} 是否存在
 */
export function isAreaExist(code, level = 3, options = {}) {
	try {
		const areaName = getAreaNameByCode(code, level, options) || ''
		//获取到的地区， 例如北京市-市辖区， 只能通过-，判断是否存在
		//如果level = 1，则判定空
		// level = 2，则判定只有一个-
		// level = 3，则判定只有两个-
		if (level == 1) {
			return areaName != ''
		} else if (level == 2) {
			return areaName.split(options.separator || '-').length == 2
		} else if (level == 3) {
			return areaName.split(options.separator || '-').length == 3
		}
		return false
	} catch (error) {
		return false
	}
}

/**
 * 把地区转换为集合， 例如 340102 转换为 [340102, 340100, 340000]
 * @param {string} code - 地区编码
 * @returns {Array} 地区集合
 */
export function getAreaSetByCode(code) {
	if (!code || code.length < 6) return []
	return [code.substring(0, 2) + '0000', code.substring(0, 4) + '00', code]
}
